import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'

// Map of notification types to their message_type values in slack_message_logs
export const NOTIFICATION_TYPE_MAP = {
  // Email Replies
  'email_positive_first': 'positiveReplyNotification',
  'email_positive_second': 'secondPositiveReplyNotification',
  'email_followup': 'followupReplyNotification',
  'email_negative': 'negativeReplyNotification',
  // LinkedIn Replies
  'linkedin_positive_first': 'linkedinPositiveReplyNotification',
  'linkedin_positive_second': 'linkedinSecondReplyNotification',
  'linkedin_followup': 'linkedinFollowupReplyNotification',
  'linkedin_negative': 'linkedinNegativeReplyNotification',
  'linkedin_neutral': 'linkedinNeutralReplyNotification',
  // Performance Reports
  'weekly_performance': 'weeklyPerformanceSummary',
  'monthly_performance': 'monthlyPerformanceSummary',
  // Outbound Reports
  'daily_outbound': 'dailyOutboundReport',
  'weekly_outbound': 'weeklyOutboundReport',
  'monthly_outbound': 'monthlyOutboundReport',
  // Other Reports
  'meeting_booked': 'meetingBookedNotification',
  // Legacy Reports
  'daily_campaign_update': 'dailyCampaignUpdate',
  'daily_send_summary': 'dailySendSummary',
  'daily_positive_summary': 'dailyPositiveSummary',
  'monthly_roi': 'monthlyROIReport',
  'weekly_objection_analysis': 'weeklyObjectionAnalysis',
  'hot_lead_alert': 'hotLeadAlert',
} as const

export interface NotificationTypeStats {
  [key: string]: number
}

export interface ClientNotificationSetting {
  id: string
  client_id: string
  client_name: string
  client_status: string
  uses_linkedin: boolean
  
  // Legacy toggles (kept for backward compatibility)
  realtime_positive_enabled: boolean
  realtime_negative_neutral_enabled: boolean
  realtime_main_enabled: boolean
  realtime_reporting_enabled: boolean
  realtime_sales_enabled: boolean
  
  // Email Reply Notifications
  email_replies_enabled: boolean
  email_positive_first_enabled: boolean
  email_positive_second_enabled: boolean
  email_followup_enabled: boolean
  email_negative_enabled: boolean
  
  // LinkedIn Reply Notifications
  linkedin_replies_enabled: boolean
  linkedin_positive_first_enabled: boolean
  linkedin_positive_second_enabled: boolean
  linkedin_followup_enabled: boolean
  linkedin_negative_enabled: boolean
  linkedin_neutral_enabled: boolean
  
  // Performance Reports
  performance_reports_enabled: boolean
  weekly_performance_enabled: boolean
  monthly_performance_enabled: boolean
  
  // Outbound Reports
  outbound_reports_enabled: boolean
  daily_outbound_enabled: boolean
  weekly_outbound_enabled: boolean
  monthly_outbound_enabled: boolean
  
  // Other Reports
  meeting_booked_enabled: boolean
  
  // Legacy Reports (disabled by default)
  legacy_reports_enabled: boolean
  daily_campaign_update_enabled: boolean
  daily_send_summary_enabled: boolean
  daily_positive_summary_enabled: boolean
  monthly_roi_enabled: boolean
  weekly_objection_analysis_enabled: boolean
  hot_lead_alert_enabled: boolean
  
  // Channel overrides
  positive_channel_override: string | null
  negative_neutral_channel_override: string | null
  main_channel_override: string | null
  reporting_channel_override: string | null
  sales_channel_override: string | null
  
  // Slack channel IDs from clients table
  slack_positive_replies_channel: string | null
  slack_positive_replies_channel_name: string | null
  slack_neutral_negative_replies_channel: string | null
  slack_neutral_negative_replies_channel_name: string | null
  slack_main_channel: string | null
  slack_main_channel_name: string | null
  slack_reporting_channel: string | null
  slack_reporting_channel_name: string | null
  slack_sales_channel: string | null
  slack_sales_channel_name: string | null
  
  // Stats - per notification type
  notification_stats: NotificationTypeStats
  notifications_sent: number
  last_notification_at: string | null
  enabled_at: string | null
  enabled_by: string | null
  notes: string | null
  updated_at: string
}

export function useClientNotificationSettings() {
  const [settings, setSettings] = useState<ClientNotificationSetting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Fetch all active clients with their notification settings and LinkedIn status
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          status,
          heyreach_api_key,
          slack_positive_replies_channel,
          slack_neutral_negative_replies_channel,
          slack_main_channel,
          slack_reporting_channel,
          slack_sales_channel
        `)
        .eq('status', 'Active')
        .not('id', 'in', '("default","1756553006828")')
        .not('name', 'ilike', '%default%')
        .not('name', 'ilike', '%outboundbuddy%')
        .order('name')

      if (clientsError) throw clientsError

      if (!clientsData || clientsData.length === 0) {
        setSettings([])
        return
      }

      // Fetch notification settings for all clients
      const { data: settingsData, error: settingsError } = await supabase
        .from('client_notification_settings')
        .select('*')

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError
      }

      // Fetch per-notification-type statistics from slack_message_logs
      const allMessageTypes = Object.values(NOTIFICATION_TYPE_MAP)
      const { data: notificationLogs, error: logsError } = await supabase
        .from('slack_message_logs')
        .select('client_id, message_type, created_at')
        .in('message_type', allMessageTypes)
        .order('created_at', { ascending: false })

      // Group statistics by client and notification type
      const statsByClient: Record<string, NotificationTypeStats> = {}
      const lastNotificationByClient: Record<string, string | null> = {}
      
      if (notificationLogs && !logsError) {
        notificationLogs.forEach(log => {
          const clientId = log.client_id
          if (!statsByClient[clientId]) {
            statsByClient[clientId] = {}
          }
          
          // Count by notification type
          const typeKey = Object.keys(NOTIFICATION_TYPE_MAP).find(
            key => NOTIFICATION_TYPE_MAP[key as keyof typeof NOTIFICATION_TYPE_MAP] === log.message_type
          )
          if (typeKey) {
            statsByClient[clientId][typeKey] = (statsByClient[clientId][typeKey] || 0) + 1
          }
          
          // Track last notification time
          if (!lastNotificationByClient[clientId]) {
            lastNotificationByClient[clientId] = log.created_at
          }
        })
      }

      // Merge data
      const mergedSettings: ClientNotificationSetting[] = clientsData.map(client => {
        const setting = settingsData?.find(s => s.client_id === client.id)
        const clientStats = statsByClient[client.id] || {}
        const totalSent = Object.values(clientStats).reduce((sum, count) => sum + count, 0)

        // Check if client uses LinkedIn
        const usesLinkedIn = !!(client.heyreach_api_key && client.heyreach_api_key.trim() !== '')

        // Channel names (using IDs directly for now)
        const mainChannelName = client.slack_main_channel
        const reportingChannelName = client.slack_reporting_channel
        const salesChannelName = client.slack_sales_channel
        const positiveChannelName = client.slack_positive_replies_channel
        const negativeNeutralChannelName = client.slack_neutral_negative_replies_channel

        return {
          id: setting?.id || crypto.randomUUID(),
          client_id: client.id,
          client_name: client.name,
          client_status: client.status,
          uses_linkedin: usesLinkedIn,
          
          // Legacy toggles (for backward compatibility)
          realtime_positive_enabled: setting?.realtime_positive_enabled ?? true,
          realtime_negative_neutral_enabled: (setting?.realtime_negative_enabled || setting?.realtime_neutral_enabled) ?? true,
          realtime_main_enabled: setting?.realtime_main_enabled ?? false,
          realtime_reporting_enabled: setting?.realtime_reporting_enabled ?? false,
          realtime_sales_enabled: setting?.realtime_sales_enabled ?? false,
          
          // Email Reply Notifications (default: true for new clients)
          email_replies_enabled: setting?.email_replies_enabled ?? true,
          email_positive_first_enabled: setting?.email_positive_first_enabled ?? true,
          email_positive_second_enabled: setting?.email_positive_second_enabled ?? true,
          email_followup_enabled: setting?.email_followup_enabled ?? true,
          email_negative_enabled: setting?.email_negative_enabled ?? true,
          
          // LinkedIn Reply Notifications (default: true for new clients)
          linkedin_replies_enabled: setting?.linkedin_replies_enabled ?? true,
          linkedin_positive_first_enabled: setting?.linkedin_positive_first_enabled ?? true,
          linkedin_positive_second_enabled: setting?.linkedin_positive_second_enabled ?? true,
          linkedin_followup_enabled: setting?.linkedin_followup_enabled ?? true,
          linkedin_negative_enabled: setting?.linkedin_negative_enabled ?? true,
          linkedin_neutral_enabled: setting?.linkedin_neutral_enabled ?? true,
          
          // Performance Reports (default: true for new clients)
          performance_reports_enabled: setting?.performance_reports_enabled ?? true,
          weekly_performance_enabled: setting?.weekly_performance_enabled ?? true,
          monthly_performance_enabled: setting?.monthly_performance_enabled ?? true,
          
          // Outbound Reports (default: true for new clients)
          outbound_reports_enabled: setting?.outbound_reports_enabled ?? true,
          daily_outbound_enabled: setting?.daily_outbound_enabled ?? true,
          weekly_outbound_enabled: setting?.weekly_outbound_enabled ?? true,
          monthly_outbound_enabled: setting?.monthly_outbound_enabled ?? true,
          
          // Other Reports (default: true for new clients)
          meeting_booked_enabled: setting?.meeting_booked_enabled ?? true,
          
          // Legacy Reports (default: false)
          legacy_reports_enabled: setting?.legacy_reports_enabled ?? false,
          daily_campaign_update_enabled: setting?.daily_campaign_update_enabled ?? false,
          daily_send_summary_enabled: setting?.daily_send_summary_enabled ?? false,
          daily_positive_summary_enabled: setting?.daily_positive_summary_enabled ?? false,
          monthly_roi_enabled: setting?.monthly_roi_enabled ?? false,
          weekly_objection_analysis_enabled: setting?.weekly_objection_analysis_enabled ?? false,
          hot_lead_alert_enabled: setting?.hot_lead_alert_enabled ?? false,
          
          // Channel overrides
          positive_channel_override: setting?.positive_channel_override || null,
          negative_neutral_channel_override: setting?.negative_channel_override || setting?.neutral_channel_override || null,
          main_channel_override: setting?.main_channel_override || null,
          reporting_channel_override: setting?.reporting_channel_override || null,
          sales_channel_override: setting?.sales_channel_override || null,
          
          // Slack channels from clients table
          slack_positive_replies_channel: client.slack_positive_replies_channel,
          slack_positive_replies_channel_name: positiveChannelName,
          slack_neutral_negative_replies_channel: client.slack_neutral_negative_replies_channel,
          slack_neutral_negative_replies_channel_name: negativeNeutralChannelName,
          slack_main_channel: client.slack_main_channel,
          slack_main_channel_name: mainChannelName,
          slack_reporting_channel: client.slack_reporting_channel,
          slack_reporting_channel_name: reportingChannelName,
          slack_sales_channel: client.slack_sales_channel,
          slack_sales_channel_name: salesChannelName,
          
          // Stats
          notification_stats: clientStats,
          notifications_sent: totalSent,
          last_notification_at: lastNotificationByClient[client.id] || null,
          enabled_at: setting?.enabled_at || null,
          enabled_by: setting?.enabled_by || null,
          notes: setting?.notes || null,
          updated_at: setting?.updated_at || new Date().toISOString()
        }
      })

      setSettings(mergedSettings)
    } catch (err: any) {
      console.error('Error fetching notification settings:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const updateClientSettings = async (
    clientId: string,
    updates: Record<string, boolean>
  ) => {
    try {
      const dbUpdates: any = { ...updates }
      
      // Handle parent toggle logic: when parent is enabled/disabled, update all children
      if ('email_replies_enabled' in updates) {
        const enabled = updates.email_replies_enabled
        dbUpdates.email_positive_first_enabled = enabled
        dbUpdates.email_positive_second_enabled = enabled
        dbUpdates.email_followup_enabled = enabled
        dbUpdates.email_negative_enabled = enabled
      }
      
      if ('linkedin_replies_enabled' in updates) {
        const enabled = updates.linkedin_replies_enabled
        dbUpdates.linkedin_positive_first_enabled = enabled
        dbUpdates.linkedin_positive_second_enabled = enabled
        dbUpdates.linkedin_followup_enabled = enabled
        dbUpdates.linkedin_negative_enabled = enabled
        dbUpdates.linkedin_neutral_enabled = enabled
      }
      
      if ('performance_reports_enabled' in updates) {
        const enabled = updates.performance_reports_enabled
        dbUpdates.weekly_performance_enabled = enabled
        dbUpdates.monthly_performance_enabled = enabled
      }
      
      if ('outbound_reports_enabled' in updates) {
        const enabled = updates.outbound_reports_enabled
        dbUpdates.daily_outbound_enabled = enabled
        dbUpdates.weekly_outbound_enabled = enabled
        dbUpdates.monthly_outbound_enabled = enabled
      }
      
      if ('legacy_reports_enabled' in updates) {
        const enabled = updates.legacy_reports_enabled
        dbUpdates.daily_campaign_update_enabled = enabled
        dbUpdates.daily_send_summary_enabled = enabled
        dbUpdates.daily_positive_summary_enabled = enabled
        dbUpdates.monthly_roi_enabled = enabled
        dbUpdates.weekly_objection_analysis_enabled = enabled
        dbUpdates.hot_lead_alert_enabled = enabled
      }
      
      // Update parent toggle based on children state (if all children are same, sync parent)
      // This is handled on the frontend when toggling individual children

      dbUpdates.updated_at = new Date().toISOString()

      const { data, error } = await supabase
        .from('client_notification_settings')
        .upsert({
          client_id: clientId,
          ...dbUpdates
        }, { onConflict: 'client_id' })
        .select()
        .single()

      if (error) throw error

      // Update local state optimistically
      setSettings(prev => prev.map(s => {
        if (s.client_id === clientId) {
          const updated = { ...s, ...dbUpdates, updated_at: new Date().toISOString() }
          // Sync parent toggles based on children
          updated.email_replies_enabled = updated.email_positive_first_enabled && 
                                          updated.email_positive_second_enabled && 
                                          updated.email_followup_enabled && 
                                          updated.email_negative_enabled
          updated.linkedin_replies_enabled = updated.linkedin_positive_first_enabled && 
                                             updated.linkedin_positive_second_enabled && 
                                             updated.linkedin_followup_enabled && 
                                             updated.linkedin_negative_enabled && 
                                             updated.linkedin_neutral_enabled
          updated.performance_reports_enabled = updated.weekly_performance_enabled && 
                                                updated.monthly_performance_enabled
          updated.outbound_reports_enabled = updated.daily_outbound_enabled && 
                                             updated.weekly_outbound_enabled && 
                                             updated.monthly_outbound_enabled
          updated.legacy_reports_enabled = updated.daily_campaign_update_enabled && 
                                          updated.daily_send_summary_enabled && 
                                          updated.daily_positive_summary_enabled && 
                                          updated.monthly_roi_enabled && 
                                          updated.weekly_objection_analysis_enabled && 
                                          updated.hot_lead_alert_enabled
          return updated
        }
        return s
      }))

      toast({
        title: 'Settings Updated',
        description: 'Notification settings have been saved.',
      })

      return true
    } catch (err: any) {
      console.error('Error updating settings:', err)
      toast({
        title: 'Update Failed',
        description: err.message,
        variant: 'destructive'
      })
      return false
    }
  }

  const bulkUpdateSettings = async (
    clientIds: string[],
    enabled: boolean
  ) => {
    try {
      const updates = clientIds.map(clientId => ({
        client_id: clientId,
        // Enable/disable all notification types
        email_replies_enabled: enabled,
        email_positive_first_enabled: enabled,
        email_positive_second_enabled: enabled,
        email_followup_enabled: enabled,
        email_negative_enabled: enabled,
        linkedin_replies_enabled: enabled,
        linkedin_positive_first_enabled: enabled,
        linkedin_positive_second_enabled: enabled,
        linkedin_followup_enabled: enabled,
        linkedin_negative_enabled: enabled,
        linkedin_neutral_enabled: enabled,
        performance_reports_enabled: enabled,
        weekly_performance_enabled: enabled,
        monthly_performance_enabled: enabled,
        outbound_reports_enabled: enabled,
        daily_outbound_enabled: enabled,
        weekly_outbound_enabled: enabled,
        monthly_outbound_enabled: enabled,
        meeting_booked_enabled: enabled,
        // Legacy reports stay disabled unless explicitly enabled
        legacy_reports_enabled: enabled ? false : false,
        enabled_at: enabled ? new Date().toISOString() : null,
        enabled_by: 'admin',
        updated_at: new Date().toISOString()
      }))

      const { error } = await supabase
        .from('client_notification_settings')
        .upsert(updates, { onConflict: 'client_id' })

      if (error) throw error

      // Refresh data
      await fetchSettings()

      toast({
        title: 'Bulk Update Complete',
        description: `${enabled ? 'Enabled' : 'Disabled'} notifications for ${clientIds.length} client(s).`,
      })

      return true
    } catch (err: any) {
      console.error('Error bulk updating:', err)
      toast({
        title: 'Bulk Update Failed',
        description: err.message,
        variant: 'destructive'
      })
      return false
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  return {
    settings,
    isLoading,
    error,
    refetch: fetchSettings,
    updateClientSettings,
    bulkUpdateSettings
  }
}
